import { useNostr } from '@nostrify/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { NostrEvent } from '@nostrify/nostrify';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { toast } from '@/hooks/useToast';
import {
  DELETION_KIND,
  REACTION_KIND,
  buildStarTags,
  isLikeReaction,
  type StarTarget,
} from '@/lib/nip25';

interface StarsResult {
  /** Number of distinct authors currently starring this event. */
  count: number;
  /** The latest active (non-retracted) "like" reaction per author. */
  reactions: NostrEvent[];
  /** The current user's own active reaction, if any. */
  userReaction?: NostrEvent;
}

/**
 * Fetches NIP-25 "like" reactions (Wikistar's "stars") for a given event,
 * honoring NIP-09 deletion requests as retractions ("unstarring").
 */
export function useStars(target: StarTarget | undefined) {
  const { nostr } = useNostr();
  const { user } = useCurrentUser();

  return useQuery<StarsResult>({
    queryKey: ['stars', target?.id],
    queryFn: async (c) => {
      if (!target) return { count: 0, reactions: [] };
      const signal = AbortSignal.any([c.signal, AbortSignal.timeout(5000)]);

      const reactionEvents = await nostr.query(
        [{ kinds: [REACTION_KIND], '#e': [target.id], limit: 500 }],
        { signal },
      );
      const likes = reactionEvents.filter(isLikeReaction);
      if (likes.length === 0) return { count: 0, reactions: [] };

      // Honor NIP-09 deletion requests retracting a star ("unstarring").
      // Only a deletion authored by the same pubkey as the reaction counts.
      const deletions = await nostr.query(
        [{ kinds: [DELETION_KIND], '#e': likes.map((r) => r.id), limit: 500 }],
        { signal },
      );
      const deletedIds = new Set(
        likes
          .filter((r) =>
            deletions.some(
              (d) => d.pubkey === r.pubkey && d.tags.some(([n, v]) => n === 'e' && v === r.id),
            ),
          )
          .map((r) => r.id),
      );

      const active = likes.filter((r) => !deletedIds.has(r.id));

      // A user may have re-reacted more than once; keep only their latest.
      const latestByAuthor = new Map<string, NostrEvent>();
      for (const reaction of active) {
        const existing = latestByAuthor.get(reaction.pubkey);
        if (!existing || reaction.created_at > existing.created_at) {
          latestByAuthor.set(reaction.pubkey, reaction);
        }
      }

      const reactions = [...latestByAuthor.values()];
      const userReaction = user ? reactions.find((r) => r.pubkey === user.pubkey) : undefined;
      return { count: reactions.length, reactions, userReaction };
    },
    enabled: Boolean(target?.id),
    staleTime: 30_000,
  });
}

/**
 * Toggles a star on `target`: publishes a NIP-25 "like" reaction (`content: '+'`)
 * to star, or a NIP-09 deletion request retracting the user's existing
 * reaction to unstar. `userReaction` should come from `useStars`.
 */
export function useToggleStar(target: StarTarget, userReaction: NostrEvent | undefined) {
  const { mutateAsync: publishEvent } = useNostrPublish();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      if (userReaction) {
        await publishEvent({
          kind: DELETION_KIND,
          content: '',
          tags: [
            ['e', userReaction.id],
            ['k', String(REACTION_KIND)],
          ],
        });
      } else {
        await publishEvent({
          kind: REACTION_KIND,
          content: '+',
          tags: buildStarTags(target),
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stars', target.id] });
    },
    onError: () => {
      toast({ title: 'Failed to update star.', variant: 'destructive' });
    },
  });
}
